// The North Carolina CFRS summary-first report view — NC gets the same polish the federal Form 3
// (Form3SummaryView), Virginia COMET (VaCometSummaryView), and Alabama FCPA (AlabamaSummaryView)
// reports got. It renders the NCSBE **CRO-1100 "Detailed Summary"** cover: the two side-by-side
// columns ("Total this Reporting Period" / "Total this Election Cycle") over the Receipts,
// Expenditures, and Additional Information line grid, so the on-page preview mirrors the paper
// form line-for-line.
//
// Every number is read straight off `detail.northCarolinaSummary` — the RP + EC cover totals the
// backend footed through the SAME `NorthCarolinaCfrsSummaryRollup` that emits the `.cfd` `SUMMARY`
// (#294) — so the treasurer's on-page cover cannot drift a cent from the file they upload. The
// view is a pure roll-up: no per-item drill-down, no edit affordances (NC itemization lives on the
// flat schedules; this is the cover).
//
// Two faithful departures from the paper CRO-1100, both to keep the on-screen arithmetic honest
// against our own data model:
//   • In-kind (goods & services) received is shown as its own Receipts line. On paper it is folded
//     into the individual/party/PAC contribution schedules; our ledger keeps it in a distinct
//     bucket, so surfacing it here lets Total Receipts foot the visible lines. It nets against
//     line 17 (In-Kind Contributions) so cash is unaffected — exactly the dual-entry the file uses.
//   • The point-in-time balances (lines 20–24: outstanding loans, debts, etc.) render only in the
//     reporting-period column; the election-cycle cell is greyed with an em-dash, matching the
//     paper form (those are close-of-period snapshots, not cycle-cumulative flows).
import type { ReactNode } from 'react';
import {
  NC_CFRS_CASH_AT_START,
  NC_CFRS_RECEIPTS,
  NC_CFRS_EXPENDITURES,
  NC_CFRS_ADDITIONAL,
} from '@signalvote/shared';
import type {
  NorthCarolinaCfrsSummary,
  NorthCarolinaCfrsSummaryRow,
  NcCfrsSummaryLine as SummaryLine,
  NcCfrsSummarySection as SummarySection,
  NcCfrsLineVariant as LineVariant,
} from '@signalvote/shared';
import { formatAmount } from '../utils';

interface NorthCarolinaSummaryViewProps {
  summary: NorthCarolinaCfrsSummary;
}

// ADR-051 — the CRO-1100 line grid (numbers, labels, CRO refs, and which lines carry an
// election-cycle column) is SHARED with the mobile report panel: a form's grid is a fact about a
// paper document, and transcribing it a second time is how the mobile panel first printed a cycle
// figure on the point-in-time balances below.
export function NorthCarolinaSummaryView({ summary }: NorthCarolinaSummaryViewProps) {
  const { reportingPeriod: rp, electionCycle: ec, electionCycleStartYear } = summary;

  return (
    <div
      className="overflow-hidden rounded-lg border border-slate-200 bg-card shadow-sm"
      data-testid="nc-summary"
    >
      {/* Header — the CRO-1100 title strip + the two-column heading. */}
      <div className="border-b border-slate-100 px-4 py-3">
        <h3 className="text-sm font-semibold text-foreground">Detailed Summary</h3>
        <p className="text-xs text-muted-foreground">
          North Carolina CFRS cover (CRO-1100) — what this report totals to, as it will be filed.
        </p>
        {electionCycleStartYear !== null && (
          <p className="mt-1 text-xs text-muted-foreground">
            Start of election cycle: January 1, {electionCycleStartYear}
          </p>
        )}
      </div>

      {/* Column headings — the two side-by-side totals that define the CRO-1100. */}
      <div className="grid grid-cols-[1fr_7.5rem_7.5rem] items-end gap-x-2 border-b border-slate-100 bg-muted/40 px-4 py-2">
        <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          Line
        </span>
        <span className="text-right text-[10px] font-semibold uppercase leading-tight tracking-wide text-muted-foreground">
          Total this Reporting Period
        </span>
        <span className="text-right text-[10px] font-semibold uppercase leading-tight tracking-wide text-muted-foreground">
          Total this Election Cycle
        </span>
      </div>

      {/* Line 4 — Cash on Hand at Start, before the Receipts section (as on the form). */}
      <LineRow line={NC_CFRS_CASH_AT_START} rp={rp} ec={ec} />

      <Section section={NC_CFRS_RECEIPTS} rp={rp} ec={ec} />
      <Section section={NC_CFRS_EXPENDITURES} rp={rp} ec={ec} />
      <Section section={NC_CFRS_ADDITIONAL} rp={rp} ec={ec} />

      <p className="border-t border-slate-100 px-4 py-2 text-[11px] leading-snug text-muted-foreground">
        In-kind contributions are reported on both the receipts and expenditures sides (line 17), so
        they net out of cash. Totals are derived from this report&apos;s itemized transactions — the
        same figures the filed <code>.cfd</code> carries.
      </p>
    </div>
  );
}

function Section({
  section,
  rp,
  ec,
}: {
  section: SummarySection;
  rp: NorthCarolinaCfrsSummaryRow;
  ec: NorthCarolinaCfrsSummaryRow;
}) {
  return (
    <div>
      <div className="bg-slate-100 px-4 py-1.5">
        <h4 className="text-xs font-bold uppercase tracking-wide text-slate-700">{section.title}</h4>
      </div>
      <div className="divide-y divide-slate-50">
        {section.lines.map((line, i) => {
          // A leading group sub-header ("Other Receipt Sources" / "Disbursements") renders above
          // the line whose number it precedes (`after` = '' means the section's first line).
          const group = section.groups?.find(
            (g) => g.after === section.lines[i - 1]?.no || (g.after === '' && i === 0),
          );
          return (
            <div key={`${line.no}-${line.field}`}>
              {group && (
                <div className="px-4 pt-2 pb-0.5 text-xs font-semibold text-foreground/80">
                  {group.label}
                </div>
              )}
              <LineRow line={line} rp={rp} ec={ec} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LineRow({
  line,
  rp,
  ec,
}: {
  line: SummaryLine;
  rp: NorthCarolinaCfrsSummaryRow;
  ec: NorthCarolinaCfrsSummaryRow;
}) {
  const variant = line.variant ?? 'normal';
  const ecApplies = line.ecApplies ?? true;

  const rowTone =
    variant === 'total'
      ? 'bg-muted/40 font-semibold text-foreground'
      : variant === 'balance'
        ? 'font-medium text-foreground'
        : 'text-foreground/90';

  return (
    <div className={`grid grid-cols-[1fr_7.5rem_7.5rem] items-baseline gap-x-2 px-4 py-1.5 text-sm ${rowTone}`}>
      <span className={`flex min-w-0 items-baseline gap-1.5 ${line.indent ? 'pl-4' : ''}`}>
        {line.no && (
          <span className="w-7 shrink-0 tabular-nums text-xs text-muted-foreground">{line.no})</span>
        )}
        <span className="min-w-0">
          {line.label}
          {line.cro && <span className="ml-1.5 text-[10px] uppercase text-muted-foreground">{line.cro}</span>}
        </span>
      </span>
      <Amount value={rp[line.field]} testId={`nc-rp-${line.field}`} variant={variant} />
      {ecApplies ? (
        <Amount value={ec[line.field]} testId={`nc-ec-${line.field}`} variant={variant} />
      ) : (
        <span
          data-testid={`nc-ec-${line.field}`}
          className="text-right tabular-nums text-muted-foreground/50"
          title="Not applicable for the election cycle — a close-of-period balance"
        >
          —
        </span>
      )}
    </div>
  );
}

function Amount({
  value,
  testId,
  variant,
}: {
  value: number;
  testId: string;
  variant: LineVariant;
}): ReactNode {
  return (
    <span
      data-testid={testId}
      className={`text-right tabular-nums ${variant === 'total' ? 'font-semibold' : ''}`}
    >
      {formatAmount(value)}
    </span>
  );
}
